import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Package, ShoppingCart, FileText, LogOut, Calendar, DollarSign, ExternalLink } from "lucide-react";
import AdminThemeToggle from "@/components/AdminThemeToggle";
import { statusSv, methodSv, statusBadgeCls, methodBadgeCls } from "@/lib/adminLabels";
import { devWarn } from "@/lib/log";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !user) navigate("/admin/login");
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem("admin_token");
    const headers = { Authorization: `Bearer ${token}` };
    Promise.all([
      axios.get(`${API}/admin/orders`, { headers }),
      axios.get(`${API}/admin/events`, { headers }),
    ]).then(([o, e]) => {
      setOrders(o.data);
      setEvents(e.data);
    }).catch(err => devWarn("Dashboard load failed:", err.message)).finally(() => setLoading(false));
  }, [user]);

  if (authLoading || !user) return <div className="min-h-screen bg-[#0A0A0A]" />;

  const totalRevenue = orders.filter(o => o.payment_status === "paid").reduce((s, o) => s + (o.total_amount || 0), 0);
  const pendingInvoices = orders.filter(o => o.payment_status === "awaiting_invoice").length;

  const handleLogout = async () => { await logout(); navigate("/admin/login"); };

  return (
    <div className="min-h-screen bg-[#0A0A0A]" data-testid="admin-dashboard">
      <nav className="bg-[#141414] border-b border-[#262626] px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Link to="/admin" className="text-[#C5A059] font-semibold text-sm tracking-[0.3em] uppercase font-['Outfit']">SERAVA ADMIN</Link>
            <div className="hidden md:flex gap-6">
              <Link to="/admin" className="text-white/60 hover:text-white text-xs tracking-wider uppercase font-['Outfit'] transition-colors">Översikt</Link>
              <Link to="/admin/events" className="text-white/60 hover:text-white text-xs tracking-wider uppercase font-['Outfit'] transition-colors">Events</Link>
              <Link to="/admin/pages" className="text-white/60 hover:text-white text-xs tracking-wider uppercase font-['Outfit'] transition-colors">Sidor</Link>
              <Link to="/admin/orders" className="text-white/60 hover:text-white text-xs tracking-wider uppercase font-['Outfit'] transition-colors">Ordrar</Link>
              <Link to="/admin/contest-entries" className="text-white/60 hover:text-white text-xs tracking-wider uppercase font-['Outfit'] transition-colors">Tävling</Link>
              <Link to="/admin/settings" className="text-white/60 hover:text-white text-xs tracking-wider uppercase font-['Outfit'] transition-colors">Inställningar</Link>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-white/40 hover:text-white text-xs font-['Outfit'] transition-colors">Visa sajten</Link>
            <AdminThemeToggle />
            <button onClick={handleLogout} data-testid="admin-logout-btn" className="text-white/40 hover:text-red-400 transition-colors"><LogOut size={18} /></button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-10">
        <h1 className="font-['Playfair_Display'] text-3xl text-white mb-8">Översikt</h1>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {[
            { icon: ShoppingCart, label: "Totalt antal ordrar", value: orders.length, color: "text-white" },
            { icon: DollarSign, label: "Intäkter (betalda)", value: `${totalRevenue.toLocaleString()} SEK`, color: "text-emerald-400" },
            { icon: FileText, label: "Väntande fakturor", value: pendingInvoices, color: "text-amber-400" },
            { icon: Calendar, label: "Aktiva events", value: events.filter(e => e.visible).length, color: "text-[#C5A059]" },
          ].map((stat) => (
            <div key={stat.label} className="bg-[#141414] border border-[#262626] p-6">
              <stat.icon size={20} className="text-white/30 mb-3" />
              <p className="text-white/50 text-xs font-['Outfit'] uppercase tracking-wider mb-1">{stat.label}</p>
              <p className={`text-2xl font-['Outfit'] font-semibold ${stat.color}`}>{loading ? "..." : stat.value}</p>
            </div>
          ))}
        </div>

        <div className="flex gap-4 mb-8">
          <Link to="/admin/events" className="btn-primary px-6 py-3 text-xs tracking-[0.15em] uppercase font-['Outfit'] flex items-center gap-2"><Package size={14} /> Hantera events</Link>
          <Link to="/admin/orders" className="btn-secondary px-6 py-3 text-xs tracking-[0.15em] uppercase font-['Outfit'] flex items-center gap-2"><ShoppingCart size={14} /> Visa ordrar</Link>
        </div>

        {/* Partner landing pages quick access */}
        <h2 className="font-['Outfit'] text-white text-lg font-medium mb-4">Partner-landningssidor</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-12" data-testid="partner-pages-quicklinks">
          {[
            { slug: "fotbollsmorgon", label: "Fotbollsmorgon", desc: "Mörkt tema · SE/EN", accent: "text-[#C5A059]" },
            { slug: "scandsports", label: "ScandSports", desc: "Mörkt tema · SE/EN", accent: "text-[#C5A059]" },
            { slug: "ica", label: "ICA", desc: "Ljust tema · ICA-stil", accent: "text-red-400" },
          ].map((p) => (
            <div key={p.slug} className="bg-[#141414] border border-[#262626] p-5 hover:border-[#C5A059]/40 transition-colors group" data-testid={`partner-card-${p.slug}`}>
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className={`text-sm font-['Outfit'] font-semibold ${p.accent}`}>{p.label}</p>
                  <p className="text-white/40 text-xs font-['Outfit'] mt-0.5">{p.desc}</p>
                </div>
                <FileText size={16} className="text-white/20 group-hover:text-[#C5A059] transition-colors" />
              </div>
              <p className="text-white/30 text-[11px] font-['Outfit'] mb-4">seravasports.com/p/{p.slug}</p>
              <div className="flex gap-2">
                <a href={`/p/${p.slug}`} target="_blank" rel="noopener noreferrer" data-testid={`partner-view-${p.slug}`} className="flex-1 text-center btn-primary px-3 py-2 text-[11px] tracking-[0.15em] uppercase font-['Outfit'] inline-flex items-center justify-center gap-1.5">
                  <ExternalLink size={12} /> Öppna live
                </a>
                <Link to="/admin/pages" className="btn-secondary px-3 py-2 text-[11px] tracking-[0.15em] uppercase font-['Outfit'] inline-flex items-center justify-center" title="Redigera i CMS">
                  Redigera
                </Link>
              </div>
            </div>
          ))}
        </div>

        {/* Recent orders */}
        <h2 className="font-['Outfit'] text-white text-lg font-medium mb-4">Senaste ordrar</h2>
        <div className="bg-[#141414] border border-[#262626] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm font-['Outfit']">
              <thead><tr className="border-b border-[#262626] text-white/50 text-xs uppercase tracking-wider">
                <th className="text-left p-4">Order</th><th className="text-left p-4">Kund</th><th className="text-left p-4">Event</th><th className="text-left p-4">Belopp</th><th className="text-left p-4">Betalsätt</th><th className="text-left p-4">Status</th>
              </tr></thead>
              <tbody>
                {orders.slice(0, 10).map(o => (
                  <tr key={o.order_number} className="border-b border-[#262626]/50 hover:bg-white/[0.02] transition-colors">
                    <td className="p-4 text-[#C5A059] font-medium">{o.order_number}</td>
                    <td className="p-4 text-white/70">{o.customer_name}</td>
                    <td className="p-4 text-white/50">{o.event_title}</td>
                    <td className="p-4 text-white font-medium">{o.total_amount?.toLocaleString()} {o.currency}</td>
                    <td className="p-4"><span className={`text-xs px-2 py-1 border ${methodBadgeCls(o.payment_method)}`}>{methodSv(o.payment_method)}</span></td>
                    <td className="p-4"><span className={`text-xs px-2 py-1 border ${statusBadgeCls(o.payment_status)}`}>{statusSv(o.payment_status)}</span></td>
                  </tr>
                ))}
                {orders.length === 0 && !loading && <tr><td colSpan="6" className="p-8 text-center text-white/30">Inga ordrar ännu</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
